/**
 * Smart Login Component - Supabase First
 * Uses Supabase as primary auth, checks beta only for migration
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, Lock, Github, Chrome, ArrowRight, Sparkles, Briefcase, User, ChevronLeft, Loader2, MessageCircle, AlertCircle, Linkedin, Eye, EyeOff, RefreshCw } from 'lucide-react';
import { useSupabaseAuth } from '../../contexts/SupabaseAuthContext';
import { getUserScenario, supabase } from '../../lib/supabase';
import { useLocation } from 'wouter';
import { DragonflyUserService } from '../../services/DragonflyUserService';
import { betaAuthService } from '../../services/BetaAuthService';
import { emailVerificationService } from '../../services/UnifiedEmailVerificationService';
import { NameCaptureStep } from './NameCaptureStep';
import { VerificationCodeInput } from './VerificationCodeInput';
import { forceAuthReset, detectAuthIssues } from '../../lib/authReset';

type AuthMode = 'detect' | 'consumer' | 'prosumer' | 'enterprise';
type AuthStep = 'email' | 'password' | 'oauth' | 'magic-link' | 'signup';

interface SmartLoginProps {
  returnTo?: string;
}

export const SmartLogin: React.FC<SmartLoginProps> = ({ returnTo = '/life' }) => {
  const [, setLocation] = useLocation();
  const { signIn, signUp, signInWithOAuth, signInWithMagicLink, resetPassword, error, clearError, loading: authLoading, isAuthenticated, user } = useSupabaseAuth();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [mode, setMode] = useState<AuthMode>('detect');
  const [step, setStep] = useState<AuthStep>('email');
  const [isLoading, setIsLoading] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [dragonflyWelcome, setDragonflyWelcome] = useState<string | null>(null);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  const [passwordResetSent, setPasswordResetSent] = useState(false);
  const [isBetaUser, setIsBetaUser] = useState(false);
  const [showBetaMigrationInfo, setShowBetaMigrationInfo] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showNameCapture, setShowNameCapture] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [lastMagicLinkTime, setLastMagicLinkTime] = useState<number>(0);
  const [magicLinkCooldown, setMagicLinkCooldown] = useState<number>(0);
  const [showWelcome, setShowWelcome] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [showAuthIssues, setShowAuthIssues] = useState(false);
  const [authIssues, setAuthIssues] = useState<string[]>([]);
  const [validatingSession, setValidatingSession] = useState(false);
  const [showVerificationCode, setShowVerificationCode] = useState(false);
  const [verificationPurpose, setVerificationPurpose] = useState<'password_reset' | 'magic_link'>('password_reset');

  // Validate and redirect if already authenticated
  // This prevents infinite loops from corrupted sessions
  useEffect(() => {
    const validateAndRedirect = async () => {
      // CRITICAL: Don't interfere with password reset flow
      const hash = window.location.hash;
      const search = window.location.search;
      const hashParams = new URLSearchParams(hash.substring(1));
      const searchParams = new URLSearchParams(search);

      const hasRecoveryToken =
        hashParams.get('type') === 'recovery' ||
        hashParams.get('access_token') ||
        searchParams.get('type') === 'recovery' ||
        searchParams.get('code') || // PKCE code for password reset
        window.location.pathname === '/reset-password' ||
        window.location.pathname === '/auth/callback' ||
        window.location.pathname === '/auth/reset-password';

      if (hasRecoveryToken) {
        console.log('[SmartLogin] Password reset flow detected, skipping SmartLogin entirely');
        console.log('[SmartLogin] Hash:', hash);
        console.log('[SmartLogin] Search:', search);
        console.log('[SmartLogin] Redirecting to auth callback...');

        // Force redirect to auth callback if we have recovery tokens or code
        if (hashParams.get('type') === 'recovery' || hashParams.get('access_token') || searchParams.get('code')) {
          const fullParams = `${search}${hash}`;
          console.log('[SmartLogin] Redirecting to: /auth/callback' + fullParams);
          window.location.href = `/auth/callback${fullParams}`;
        }
        return;
      }

      if (isAuthenticated && user) {
        console.log('[SmartLogin] User appears authenticated, validating session...');
        setValidatingSession(true);

        try {
          // Add 5-second timeout to prevent hanging
          const sessionPromise = supabase.auth.getSession();
          const timeoutPromise = new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Session validation timeout')), 5000)
          );

          const { data: { session }, error } = await Promise.race([
            sessionPromise,
            timeoutPromise
          ]) as any;

          if (error) {
            console.error('[SmartLogin] Session validation error:', error);
            console.log('[SmartLogin] Clearing corrupted session...');
            await forceAuthReset();
            return;
          }

          if (session && session.user) {
            console.log('[SmartLogin] ✅ Session valid, redirecting to:', returnTo);
            setValidatingSession(false);
            setLocation(returnTo);
          } else {
            console.warn('[SmartLogin] ⚠️ No valid session found, clearing auth state...');
            console.log('[SmartLogin] Clearing corrupted session...');
            await forceAuthReset();
          }
        } catch (err: any) {
          if (err.message?.includes('timeout')) {
            console.error('[SmartLogin] ⏱️ Session validation timed out - forcing reset');
          } else {
            console.error('[SmartLogin] Session validation failed:', err);
          }
          console.log('[SmartLogin] Clearing corrupted session...');
          await forceAuthReset();
        }
      }
    };

    validateAndRedirect();
  }, [isAuthenticated, user, returnTo, setLocation]);

  // Check for email verification success on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const verified = urlParams.get('verified');
    const showWelcomeFlag = localStorage.getItem('show_welcome_message');
    const error = urlParams.get('error');
    const hint = urlParams.get('hint');

    if (verified === 'true' && showWelcomeFlag === 'true') {
      setEmailVerified(true);
      setShowWelcome(true);
      // Clear the flag so it doesn't show again
      localStorage.removeItem('show_welcome_message');

      // Clear URL params
      window.history.replaceState({}, '', '/login');
    }

    // Check for auth errors (PKCE, OAuth timeout, etc.)
    if (error) {
      const fallback = urlParams.get('fallback');
      const urlEmail = urlParams.get('email'); // Get email from URL if present

      // Handle OAuth timeout specifically
      if (error === 'oauth_timeout') {
        console.log('[SmartLogin] OAuth timeout detected, showing retry option');
        setLocalError('oauth_timeout');

        if (urlEmail) {
          setEmail(urlEmail);
        }

        // No verification code fallback - just show error and let user retry OAuth
        window.history.replaceState({}, '', '/login');
        return;
      }

      // Check for PKCE errors (magic link/password reset in different browser)
      if (error === 'pkce_verifier_missing' || error === 'pkce_error') {
        // If fallback=verification_code, show verification code input
        if (fallback === 'verification_code') {
          console.log('[SmartLogin] PKCE failed, showing verification code fallback');

          // Set email from URL or localStorage
          if (urlEmail) {
            setEmail(urlEmail);
          } else {
            // Try to get email from localStorage (stored during password reset request)
            const storedEmail = localStorage.getItem('password_reset_email');
            if (storedEmail) {
              setEmail(storedEmail);
            }
          }

          setShowVerificationCode(true);
          setVerificationPurpose('password_reset');
          // Clear URL params
          window.history.replaceState({}, '', '/login');
          return;
        }

        // Show verification code as an alternative option
        setLocalError('pkce_browser_mismatch'); // Special error code to trigger button
        if (urlEmail) {
          setEmail(urlEmail);
        }
        // Clear URL params
        window.history.replaceState({}, '', '/login');
      }
    }

    // Check for auth issues on mount
    const issues = detectAuthIssues();
    if (issues.hasIssues) {
      console.log('[SmartLogin] Auth issues detected:', issues.issues);
      setAuthIssues(issues.issues);
      setShowAuthIssues(true);
    }
  }, []);

  useEffect(() => {
    // Clear errors when component unmounts or step changes
    return () => {
      clearError();
      setLocalError(null);
    };
  }, [step, clearError]);

  // Magic link cooldown timer
  useEffect(() => {
    if (magicLinkCooldown > 0) {
      const timer = setInterval(() => {
        setMagicLinkCooldown(prev => Math.max(0, prev - 1));
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [magicLinkCooldown]);

  // Detect user scenario from email
  const detectScenario = (email: string): AuthMode => {
    const domain = email.split('@')[1];
    const enterpriseDomains = import.meta.env.VITE_ENTERPRISE_DOMAINS?.split(',') || [];
    
    if (enterpriseDomains.includes(domain)) {
      return 'enterprise';
    }
    
    const scenario = getUserScenario(email, {});
    return scenario || 'consumer';
  };
  
  // Handle email submission
  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      setLocalError('Please enter your email');
      return;
    }

    // Move to password step immediately
    const detectedMode = detectScenario(email);
    setMode(detectedMode);
    setStep('password');

    // Check if this is a Dragonfly user (for welcome message) - non-blocking
    DragonflyUserService.checkDragonflyUser(email).then(dragonflyInfo => {
      if (dragonflyInfo.isDragonflyUser && dragonflyInfo.welcomeMessage) {
        setDragonflyWelcome(dragonflyInfo.welcomeMessage);
        console.log('Returning user detected:', dragonflyInfo);
      }
    }).catch(err => {
      console.log('Dragonfly check failed (non-blocking):', err.message);
    });
  };

  // Handle password login - SUPABASE FIRST!
  const handlePasswordLogin = async (e: React.FormEvent) => {
    console.log('🔐 handlePasswordLogin called!');
    e.preventDefault();
    console.log('🔐 Password login attempt for:', email);
    console.log('🔐 Password length:', password?.length || 0);
    console.log('🔐 Return to:', returnTo);
    
    if (!password) {
      console.error('🔐 No password entered!');
      setLocalError('Please enter your password');
      return;
    }
    
    setIsLoading(true);
    setLocalError(null);

    try {
      // PRIMARY: Try Supabase authentication first
      console.log('🔐 Attempting Supabase authentication...');
      
      try {
        await signIn(email, password);
        console.log('🔐 Supabase sign in successful!');

        // Optional: Check if they're also a beta user (for tracking)
        betaAuthService.checkBetaSession(email).then(status => {
          if (status.authenticated) {
            console.log('🔐 User is also on beta platform');
            localStorage.setItem('also_beta_user', 'true');
          }
        }).catch(() => {
          // Ignore beta check errors
        });

        // Check if user needs name capture (with timeout and error handling)
        try {
          const { data: { user } } = await supabase.auth.getUser();

          if (user) {
            console.log('🔐 Checking profile for name capture...');

            // Use Promise.race to add timeout protection
            const profilePromise = supabase
              .from('profiles')
              .select('first_name')
              .eq('id', user.id)
              .single();

            const timeoutPromise = new Promise((_, reject) =>
              setTimeout(() => reject(new Error('Profile check timeout')), 5000)
            );

            try {
              const { data: profile, error: profileError } = await Promise.race([
                profilePromise,
                timeoutPromise
              ]) as any;

              if (profileError) {
                console.warn('🔐 Profile check failed, proceeding with redirect:', profileError.message);
              } else if (!profile?.first_name) {
                console.log('🔐 User needs name capture');
                setCurrentUserId(user.id);
                setShowNameCapture(true);
                setIsLoading(false);
                return;
              } else {
                console.log('🔐 User has first_name:', profile.first_name);
              }
            } catch (profileCheckError: any) {
              console.warn('🔐 Profile check failed (timeout or error), proceeding with redirect:', profileCheckError.message);
              // Continue to redirect even if profile check fails
            }
          }
        } catch (userCheckError: any) {
          console.warn('🔐 Failed to get user, proceeding with redirect anyway:', userCheckError.message);
          // Continue to redirect even if we can't get the user
        }

        // Redirect after successful login
        console.log('🔐 Redirecting to:', returnTo || '/');
        setIsLoading(false);
        setLocation(returnTo || '/');
        
      } catch (supabaseError: any) {
        console.log('🔐 Supabase sign in failed:', supabaseError.message);

        // Handle email not confirmed error (should not happen with email confirmation disabled)
        if (supabaseError.message?.includes('Email not confirmed')) {
          console.log('🔐 Email confirmation issue detected');
          setLocalError('⚠️ Account verification required. Please contact support if this persists.');
          setIsLoading(false);
          return;
        }

        // If Supabase fails with invalid credentials, check if they're a beta user we can migrate
        if (supabaseError.message?.includes('Invalid login credentials')) {
          console.log('🔐 User not in Supabase, checking if they exist on beta...');
          
          try {
            // Try to authenticate with beta to verify they're a real user
            const betaAuth = await betaAuthService.authenticateWithBeta(email, password);

            if (betaAuth.success) {
              console.log('🔐 ✅ Valid beta user! Migrating to Supabase...', betaAuth);
              setIsBetaUser(true);
              setShowBetaMigrationInfo(true);

              // Check if profile already exists (from previous migration)
              const { data: existingProfile } = await supabase
                .from('profiles')
                .select('id, email')
                .eq('email', email)
                .single();

              if (existingProfile) {
                console.log('🔐 ⚠️ Orphaned profile exists! Calling server to migrate...', existingProfile);

                // Call server API to migrate orphaned profile
                const firstName = betaAuth.user?.firstName || email.split('@')[0];
                const lastName = betaAuth.user?.lastName || '';

                console.log('🔐 Calling migration API with:', { email, firstName, lastName });

                // Create abort controller for timeout
                const controller = new AbortController();
                const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

                try {
                  const migrateResponse = await fetch('/api/migrate-orphaned-profile', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      email,
                      password,
                      firstName,
                      lastName,
                      betaUserId: betaAuth.user?.id || email
                    }),
                    signal: controller.signal
                  });

                  clearTimeout(timeoutId);

                  console.log('🔐 Migration API response status:', migrateResponse.status);

                  if (!migrateResponse.ok) {
                    const errorData = await migrateResponse.json();
                    console.error('🔐 Migration API error:', errorData);
                    throw new Error(errorData.error || 'Migration failed');
                  }

                  const migrateData = await migrateResponse.json();
                  console.log('🔐 ✅ Server migration complete:', migrateData);
                } catch (fetchError: any) {
                  clearTimeout(timeoutId);

                  if (fetchError.name === 'AbortError') {
                    console.error('🔐 Migration API timeout after 30 seconds');
                    throw new Error('Migration timed out. Please try again or contact support.');
                  }

                  console.error('🔐 Migration API fetch error:', fetchError);
                  throw fetchError;
                }

                // Now sign in with the new account
                const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
                  email,
                  password
                });

                if (signInError) {
                  throw signInError;
                }

                console.log('🔐 ✅ Migration complete! Redirecting...');
                setLocation(returnTo || '/');
                return;
              }

              // No existing profile - proceed with normal signup
              console.log('🔐 No existing profile found, creating new account...');

              // Create Supabase account for the beta user
              try {
                const firstName = betaAuth.user?.firstName || email.split('@')[0];
                const lastName = betaAuth.user?.lastName || '';

                console.log('🔐 Creating Supabase account with data:', {
                  email,
                  firstName,
                  lastName,
                  metadata: {
                    migrated_from: 'beta.redheli.com',
                    beta_user_id: betaAuth.user?.id || email,
                    migration_date: new Date().toISOString()
                  }
                });

                const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
                  email,
                  password,
                  options: {
                    data: {
                      firstName,
                      lastName,
                      migrated_from: 'beta.redheli.com',
                      beta_user_id: betaAuth.user?.id || email,
                      migration_date: new Date().toISOString()
                    }
                  }
                });

                console.log('🔐 Sign up response:', { signUpData, signUpError });
                
                if (signUpError) {
                  throw signUpError;
                }
                
                // After successful signup, try to sign in
                console.log('🔐 Account created or exists, signing in...');
                
                // Sign in with Supabase
                const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
                  email,
                  password
                });
                
                console.log('🔐 Sign in response:', { signInData, signInError });
                
                if (signInError) {
                  // If sign in fails after successful signup, reset the password
                  console.log('🔐 Sign in failed after signup. Initiating password reset...');

                  // Send password reset email
                  const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
                    redirectTo: `${window.location.origin}/auth/callback`
                  });
                  
                  if (!resetError) {
                    console.log('🔐 Password reset email sent');
                    setLocalError('🎉 Welcome to Hover! Your Beta account has been successfully migrated. Please check your email to set a Hover-specific password. Your Beta login remains unchanged.');
                    setVerificationSent(true);
                    setIsLoading(false);
                    return;
                  }
                  
                  // If email needs confirmation
                  if (signInError.message?.includes('Email not confirmed')) {
                    console.log('🔐 Email confirmation required');
                    setLocalError('Please check your email to confirm your account before signing in.');
                    setVerificationSent(true);
                    return;
                  }
                  throw signInError;
                }
                
                // Mark as migrated
                localStorage.setItem('beta_migrated', 'true');
                localStorage.setItem('migration_date', new Date().toISOString());
                
                console.log('🔐 Migration complete! User logged in.');
                
              } catch (migrationError: any) {
                console.error('🔐 Migration error:', migrationError);
                
                // If the error is about user already existing, try direct sign in
                if (migrationError.message?.includes('already registered') || 
                    migrationError.message?.includes('User already registered') ||
                    migrationError.code === 'user_already_exists') {
                  console.log('🔐 User may already exist, trying direct sign in...');
                  
                  try {
                    const { error: retryError } = await supabase.auth.signInWithPassword({
                      email,
                      password
                    });
                    
                    if (retryError) {
                      throw retryError;
                    }
                    
                    console.log('🔐 Direct sign in successful after migration attempt');
                  } catch (retryErr) {
                    console.error('🔐 Direct sign in also failed:', retryErr);
                    throw new Error('Unable to authenticate. Please try again.');
                  }
                } else {
                  throw migrationError;
                }
              }
            } else {
              // Not a valid beta user either
              console.log('🔐 Not a valid beta user either');
              throw new Error('Invalid email or password');
            }
          } catch (betaError) {
            console.error('🔐 Beta check failed:', betaError);
            // Beta check failed (CORS, network, etc), show original error
            throw new Error('Invalid email or password');
          }
        } else {
          // Some other Supabase error
          throw supabaseError;
        }
      }

      // After successful login, redirect immediately (don't block on Dragonfly check)
      console.log('🔐 Login successful! Redirecting to:', returnTo);

      // Check Dragonfly status asynchronously in background (don't await)
      DragonflyUserService.checkDragonflyUser(email).then(dragonflyInfo => {
        if (dragonflyInfo.isDragonflyUser) {
          console.log('🔐 Dragonfly user detected:', dragonflyInfo.sourcePlatform);
          localStorage.setItem('dragonfly_user', 'true');
          localStorage.setItem('dragonfly_source', dragonflyInfo.sourcePlatform || 'beta');
        }
      }).catch(err => {
        console.log('🔐 Dragonfly check failed (non-blocking):', err.message);
      });

      // Redirect immediately based on returnTo path
      setLocation(returnTo);

    } catch (err: any) {
      console.error('Login error:', err);
      let errorMessage = 'Failed to sign in. Please check your credentials.';
      
      if (err.message) {
        if (err.message.includes('Email not confirmed')) {
          errorMessage = 'Please check your email to confirm your account before signing in.';
        } else if (err.message.includes('Invalid')) {
          errorMessage = 'Invalid email or password. Please try again.';
        } else {
          errorMessage = err.message;
        }
      }
      
      setLocalError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle signup
  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setLocalError(null);

    try {
      console.log('[SIGNUP] Creating Supabase account for:', email);

      // Create account in Supabase (primary)
      const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            firstName,
            lastName,
            auth_source: 'hover',
            signup_date: new Date().toISOString()
          },
          emailRedirectTo: `${window.location.origin}/auth/callback`
        }
      });

      if (signUpError) {
        console.error('[SIGNUP] Supabase signup error:', signUpError);
        throw signUpError;
      }

      console.log('[SIGNUP] ✅ Supabase account created successfully');

      // Email confirmation disabled - user is automatically logged in
      // Check if user is authenticated
      if (signUpData.user && signUpData.session) {
        console.log('[SIGNUP] User automatically logged in, redirecting...');

        // Redirect to returnTo or default location
        setTimeout(() => {
          setLocation(returnTo || '/life');
        }, 500);
      } else {
        // Fallback: show verification message (shouldn't happen with email confirmation disabled)
        console.log('[SIGNUP] No session created, showing verification message');
        setVerificationSent(true);
        setStep('email');
      }

      setLocalError(null); // Clear any errors

      // Optional: Register on beta in the background (don't await, don't block on errors)
      // This is fire-and-forget to avoid rate limiting
      setTimeout(() => {
        betaAuthService.registerOnBeta({
          name: `${firstName} ${lastName}`.trim(),
          email: email,
          username: email.split('@')[0],
          password: password,
          phone: ''
        }).then(() => {
          console.log('[SIGNUP] Also registered on beta for compatibility');
        }).catch((err) => {
          console.log('[SIGNUP] Beta registration failed (ignored):', err.message);
        });
      }, 2000); // Wait 2 seconds to avoid rate limiting

    } catch (err: any) {
      console.error('[SIGNUP] Error:', err);

      let errorMessage = 'Failed to create account. Please try again.';

      if (err.message?.includes('already registered') || err.message?.includes('User already registered')) {
        errorMessage = 'An account with this email already exists. Please sign in instead.';
        setStep('password'); // Switch to password login
      } else if (err.message?.includes('rate limit')) {
        errorMessage = 'Too many requests. Please wait a minute and try again.';
      } else if (err.message) {
        errorMessage = err.message;
      }

      setLocalError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle OAuth login
  const handleOAuthLogin = async (provider: 'google' | 'github' | 'kakao' | 'linkedin') => {
    setIsLoading(true);
    setLocalError(null);

    try {
      await signInWithOAuth(provider);
    } catch (err: any) {
      console.error('OAuth error:', err);
      setLocalError(err.message || 'Failed to sign in with OAuth provider.');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle magic link
  const handleMagicLink = async () => {
    // Check cooldown
    const now = Date.now();
    const timeSinceLastRequest = (now - lastMagicLinkTime) / 1000;
    const remainingCooldown = Math.max(0, 40 - timeSinceLastRequest);

    if (remainingCooldown > 0) {
      setLocalError(`Please wait ${Math.ceil(remainingCooldown)} seconds before requesting another magic link.`);
      setMagicLinkCooldown(Math.ceil(remainingCooldown));
      return;
    }

    setIsLoading(true);
    setLocalError(null);

    try {
      await signInWithMagicLink(email);
      setMagicLinkSent(true);
      setLocalError(null);
      setLastMagicLinkTime(Date.now());
      setMagicLinkCooldown(40);
    } catch (err: any) {
      console.error('Magic link error:', err);
      if (err.message?.includes('60 seconds') || err.message?.includes('40 seconds')) {
        setLocalError('Too many requests. Please wait 60 seconds before trying again.');
        setMagicLinkCooldown(60);
      } else {
        setLocalError(err.message || 'Failed to send magic link. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const displayError = localError || error;

  // Rest of the component remains the same (UI)
  // Handle name capture completion
  const handleNameCaptureComplete = (firstName: string, lastName: string) => {
    console.log('[SmartLogin] Name capture complete:', firstName, lastName);
    setShowNameCapture(false);
    setLocation(returnTo || '/');
  };

  // Show verification code input if PKCE failed
  if (showVerificationCode) {
    return (
      <VerificationCodeInput
        email={email}
        purpose={verificationPurpose}
        onSuccess={(sessionUrl) => {
          console.log('[SmartLogin] Verification successful, redirecting to session URL');
          // Extract tokens from session URL and redirect
          window.location.href = sessionUrl;
        }}
        onCancel={() => {
          setShowVerificationCode(false);
          setStep('email');
          setEmail('');
        }}
      />
    );
  }

  // Show name capture step if needed
  if (showNameCapture && currentUserId) {
    return (
      <NameCaptureStep
        userId={currentUserId}
        email={email}
        onComplete={handleNameCaptureComplete}
      />
    );
  }

  // Show loading state if already authenticated (while validating/redirecting)
  if (isAuthenticated && user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-indigo-900 to-black flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center"
        >
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-purple-400 mx-auto mb-4"></div>
          <p className="text-lg text-purple-200">
            {validatingSession ? 'Validating session...' : 'Already signed in, redirecting...'}
          </p>
          <p className="text-sm text-purple-300 mt-2">
            {user.email}
          </p>
          {validatingSession && (
            <p className="text-xs text-purple-400 mt-3">
              Verifying your authentication status
            </p>
          )}
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-indigo-900 to-black flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        {/* Logo/Title */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">
            Red Helicopter
          </h1>
          <p className="text-purple-300">Transform and Thrive</p>
        </div>

        {/* Auth Card */}
        <motion.div
          className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 shadow-2xl border border-purple-500/20"
          layout
        >
          <AnimatePresence mode="wait">
            {/* Email Input Step */}
            {step === 'email' && (
              <motion.form
                key="email"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                onSubmit={handleEmailSubmit}
              >
                <h2 className="text-2xl font-semibold text-white mb-6">Welcome</h2>

                {/* Welcome message after email verification */}
                {showWelcome && emailVerified && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-6 p-4 bg-gradient-to-r from-green-600/20 to-emerald-600/20 border border-green-500/30 rounded-lg"
                  >
                    <div className="flex items-start gap-3">
                      <Sparkles className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-white font-medium mb-1">🎉 Email Verified Successfully!</p>
                        <p className="text-green-200 text-sm leading-relaxed">
                          Your account is now active. Please enter your password below to sign in and begin your journey.
                        </p>
                      </div>
                    </div>
                  </motion.div>
                )}

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-purple-200 mb-2">
                      Email Address
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-purple-400 w-5 h-5" />
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full pl-10 pr-4 py-3 bg-white/5 border border-purple-500/30 rounded-lg text-white placeholder-purple-300/50 focus:outline-none focus:border-purple-400 focus:bg-white/10 transition text-base sm:text-sm"
                        placeholder="you@example.com"
                        autoFocus
                        required
                      />
                    </div>
                  </div>

                  {displayError && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                    >
                      {displayError === 'oauth_timeout' ? (
                        <div className="p-4 bg-orange-500/20 border border-orange-500/50 rounded-lg">
                          <div className="flex items-start gap-3 mb-3">
                            <AlertCircle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="text-orange-200 font-medium mb-1">Sign-In Taking Too Long</p>
                              <p className="text-orange-300/80 text-xs leading-relaxed">
                                Google sign-in is experiencing delays. We can send you a verification code to complete sign-in faster.
                              </p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              console.log('[SmartLogin] User clicked verification code for OAuth timeout');
                              if (!email) {
                                setLocalError('Please enter your email address first');
                                return;
                              }
                              setShowVerificationCode(true);
                              setVerificationPurpose('magic_link');
                              setLocalError(null);
                            }}
                            className="w-full py-2 bg-orange-600/30 hover:bg-orange-600/50 border border-orange-500/50 text-orange-200 font-medium rounded-lg transition flex items-center justify-center gap-2"
                          >
                            <MessageCircle className="w-4 h-4" />
                            Send Verification Code Instead
                          </button>
                        </div>
                      ) : displayError === 'pkce_browser_mismatch' ? (
                        <div className="p-4 bg-yellow-500/20 border border-yellow-500/50 rounded-lg">
                          <div className="flex items-start gap-3 mb-3">
                            <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="text-yellow-200 font-medium mb-1">Link Opened in Different Browser</p>
                              <p className="text-yellow-300/80 text-xs leading-relaxed">
                                For security, password reset links must be opened in the same browser where you requested them.
                              </p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              console.log('[SmartLogin] User clicked verification code fallback');
                              setShowVerificationCode(true);
                              setVerificationPurpose('password_reset');
                              setLocalError(null);
                            }}
                            className="w-full py-2 bg-yellow-600/30 hover:bg-yellow-600/50 border border-yellow-500/50 text-yellow-200 font-medium rounded-lg transition flex items-center justify-center gap-2"
                          >
                            <Mail className="w-4 h-4" />
                            Send Verification Code Instead
                          </button>
                        </div>
                      ) : (
                        <div className="p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-200 text-sm flex items-start gap-2">
                          <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                          <span>{displayError}</span>
                        </div>
                      )}
                    </motion.div>
                  )}

                  {/* Auth Issues Warning */}
                  {showAuthIssues && authIssues.length > 0 && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="p-4 bg-yellow-500/20 border border-yellow-500/50 rounded-lg"
                    >
                      <div className="flex items-start gap-3 mb-3">
                        <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-yellow-200 font-medium mb-1">Login Issues Detected</p>
                          <p className="text-yellow-300/80 text-xs leading-relaxed">
                            We found stale auth data that might prevent login:
                          </p>
                          <ul className="text-yellow-300/70 text-xs mt-2 space-y-1">
                            {authIssues.map((issue, idx) => (
                              <li key={idx}>• {issue}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          console.log('[SmartLogin] User clicked Clear Login Data');
                          forceAuthReset();
                        }}
                        className="w-full py-2 bg-yellow-600/30 hover:bg-yellow-600/50 border border-yellow-500/50 text-yellow-200 font-medium rounded-lg transition flex items-center justify-center gap-2"
                      >
                        <RefreshCw className="w-4 h-4" />
                        Clear Login Data & Reset
                      </button>
                    </motion.div>
                  )}

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-lg hover:from-purple-700 hover:to-pink-700 transition flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {isLoading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        Continue
                        <ArrowRight className="w-5 h-5" />
                      </>
                    )}
                  </button>
                </div>

                {/* OAuth Divider */}
                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-purple-500/30" />
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="bg-gradient-to-br from-purple-900 via-indigo-900 to-blue-900 px-3 text-purple-300">
                      Or continue with
                    </span>
                  </div>
                </div>

                {/* OAuth Buttons */}
                <div className="grid grid-cols-3 gap-3">
                  <button
                    type="button"
                    onClick={() => handleOAuthLogin('google')}
                    disabled={isLoading}
                    className="px-4 py-3 bg-white/10 border border-purple-500/30 text-white rounded-lg hover:bg-white/20 transition flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    <Chrome className="w-5 h-5" />
                    <span className="text-sm font-medium">Google</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleOAuthLogin('linkedin')}
                    disabled={isLoading}
                    className="px-4 py-3 bg-white/10 border border-purple-500/30 text-white rounded-lg hover:bg-white/20 transition flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    <Linkedin className="w-5 h-5" />
                    <span className="text-sm font-medium">LinkedIn</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleOAuthLogin('github')}
                    disabled={isLoading}
                    className="px-4 py-3 bg-white/10 border border-purple-500/30 text-white rounded-lg hover:bg-white/20 transition flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    <Github className="w-5 h-5" />
                    <span className="text-sm font-medium">GitHub</span>
                  </button>
                </div>

                <div className="mt-6 text-center">
                  <p className="text-purple-300 text-sm">
                    New here?{' '}
                    <button
                      type="button"
                      onClick={() => setStep('signup')}
                      className="text-pink-400 hover:text-pink-300 font-medium"
                    >
                      Create an account
                    </button>
                  </p>
                </div>
              </motion.form>
            )}

            {/* Password Step */}
            {step === 'password' && !magicLinkSent && !passwordResetSent && (
              <motion.form
                key="password"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                onSubmit={handlePasswordLogin}
              >
                <button
                  type="button"
                  onClick={() => setStep('email')}
                  className="flex items-center gap-2 text-purple-300 hover:text-white mb-4 transition"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Back
                </button>

                <h2 className="text-2xl font-semibold text-white mb-2">Welcome back!</h2>
                <p className="text-purple-300 mb-6">{email}</p>

                {/* Show personalized welcome for Dragonfly users */}
                {dragonflyWelcome && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-6 p-4 bg-gradient-to-r from-purple-600/20 to-pink-600/20 border border-purple-500/30 rounded-lg"
                  >
                    <p className="text-white text-sm leading-relaxed">{dragonflyWelcome}</p>
                  </motion.div>
                )}
                
                {/* Show beta user migration info */}
                {showBetaMigrationInfo && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-6 p-4 bg-blue-600/20 border border-blue-500/30 rounded-lg"
                  >
                    <div className="flex items-start gap-3">
                      <Sparkles className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-white font-medium mb-1">Welcome from Beta!</p>
                        <p className="text-blue-200 text-sm leading-relaxed mb-2">
                          We're creating your Hover account with the same credentials. 
                          Your Beta account remains unchanged and you can continue using both platforms.
                        </p>
                        <p className="text-blue-200 text-xs">
                          <strong>Note:</strong> If you're having trouble signing in, click "Forgot password?" below 
                          to set a new password for your Hover account.
                        </p>
                      </div>
                    </div>
                  </motion.div>
                )}

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-purple-200 mb-2">
                      Password
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-purple-400 w-5 h-5" />
                      <input
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full pl-10 pr-12 py-3 bg-white/5 border border-purple-500/30 rounded-lg text-white placeholder-purple-300/50 focus:outline-none focus:border-purple-400 focus:bg-white/10 transition"
                        placeholder="Enter your password"
                        autoFocus
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-purple-400 hover:text-purple-300 transition"
                        aria-label={showPassword ? "Hide password" : "Show password"}
                      >
                        {showPassword ? (
                          <EyeOff className="w-5 h-5" />
                        ) : (
                          <Eye className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                  </div>

                  {displayError && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-200 text-sm flex items-start gap-2"
                    >
                      <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                      <span>{displayError}</span>
                    </motion.div>
                  )}

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-lg hover:from-purple-700 hover:to-pink-700 transition flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {isLoading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        Sign In
                        <User className="w-5 h-5" />
                      </>
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={handleMagicLink}
                    disabled={isLoading || magicLinkCooldown > 0}
                    className="w-full py-3 bg-white/5 border border-purple-500/30 text-purple-300 rounded-lg hover:bg-white/10 transition flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    <Mail className="w-5 h-5" />
                    {magicLinkCooldown > 0 ? `Wait ${magicLinkCooldown}s` : 'Send Magic Link'}
                  </button>
                </div>

                <div className="mt-6 text-center">
                  <button
                    type="button"
                    onClick={async () => {
                      if (!email) {
                        setLocalError('Please enter your email address first');
                        return;
                      }

                      try {
                        console.log('[FORGOT PASSWORD] Initiating password reset for:', email);
                        setIsLoading(true);
                        setLocalError(null);

                        // Store email in localStorage for cross-browser verification code fallback
                        localStorage.setItem('password_reset_email', email);

                        await resetPassword(email);

                        console.log('[FORGOT PASSWORD] ✅ Password reset email sent successfully');
                        setPasswordResetSent(true);
                        setMagicLinkSent(false);
                      } catch (err: any) {
                        console.error('[FORGOT PASSWORD] ❌ Error:', err);

                        // Provide more specific error messages
                        let errorMessage = 'Unable to send reset email. Please try again.';

                        if (err?.message?.includes('Email not found')) {
                          errorMessage = 'No account found with this email address.';
                        } else if (err?.message?.includes('rate limit')) {
                          errorMessage = 'Too many requests. Please wait a few minutes and try again.';
                        } else if (err?.message) {
                          errorMessage = err.message;
                        }

                        setLocalError(errorMessage);
                      } finally {
                        setIsLoading(false);
                      }
                    }}
                    disabled={isLoading || !email}
                    className="text-purple-300 hover:text-white text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? 'Sending...' : `Forgot password?${showBetaMigrationInfo ? ' (Reset Hover password)' : ''}`}
                  </button>
                </div>
              </motion.form>
            )}

            {/* Magic Link Sent */}
            {magicLinkSent && (
              <motion.div
                key="magic-link-sent"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="text-center"
              >
                <div className="mb-6">
                  <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Mail className="w-8 h-8 text-green-400" />
                  </div>
                  <h2 className="text-2xl font-semibold text-white mb-2">Check Your Email</h2>
                  <p className="text-purple-300 mb-6">
                    We've sent a magic link to {email}
                  </p>

                  {/* Important: Same browser reminder */}
                  <div className="bg-blue-900/30 border border-blue-500/30 rounded-lg p-4 mb-4 text-left">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-blue-200 text-sm font-medium mb-1">Important:</p>
                        <p className="text-blue-300/80 text-xs leading-relaxed">
                          Click the link in your email <strong>in THIS browser</strong>. Opening it in a different browser will not work due to security protections.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => {
                    setMagicLinkSent(false);
                    setStep('email');
                  }}
                  className="text-purple-300 hover:text-white"
                >
                  Try a different email
                </button>
              </motion.div>
            )}

            {/* Password Reset Email Sent */}
            {passwordResetSent && (
              <motion.div
                key="password-reset-sent"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="text-center"
              >
                <div className="mb-6">
                  <div className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Lock className="w-8 h-8 text-blue-400" />
                  </div>
                  <h2 className="text-2xl font-semibold text-white mb-2">Password Reset Email Sent!</h2>
                  <p className="text-purple-300 mb-4">
                    We've sent password reset instructions to:
                  </p>
                  <p className="text-white font-medium mb-6">{email}</p>

                  <div className="bg-purple-900/30 border border-purple-500/30 rounded-lg p-4 mb-4 text-left">
                    <h3 className="text-white font-semibold mb-2">Next Steps:</h3>
                    <ol className="text-purple-200 text-sm space-y-2">
                      <li>1. Check your email inbox (and spam folder)</li>
                      <li>2. Click the "Reset Password" link <strong>in THIS browser</strong></li>
                      <li>3. You'll be redirected to set your new password</li>
                      <li>4. After setting your password, you can sign in</li>
                    </ol>
                  </div>

                  {/* Important: Same browser reminder */}
                  <div className="bg-yellow-900/30 border border-yellow-500/30 rounded-lg p-3 mb-4 text-left">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 text-yellow-400 flex-shrink-0 mt-0.5" />
                      <p className="text-yellow-200/80 text-xs leading-relaxed">
                        <strong>Important:</strong> Click the reset link in the same browser where you requested it. Opening it in a different browser will not work.
                      </p>
                    </div>
                  </div>

                  <p className="text-purple-300 text-sm mb-4">
                    The reset link will expire in 1 hour for security reasons.
                  </p>
                </div>

                <div className="space-y-3">
                  <button
                    onClick={() => {
                      setPasswordResetSent(false);
                      setStep('email');
                      setEmail('');
                    }}
                    className="text-purple-300 hover:text-white block mx-auto"
                  >
                    Try a different email
                  </button>
                  
                  <button
                    onClick={() => {
                      setPasswordResetSent(false);
                      setStep('password');
                    }}
                    className="text-purple-300 hover:text-white block mx-auto text-sm"
                  >
                    Back to sign in
                  </button>
                </div>
              </motion.div>
            )}

            {/* Verification Email Sent (after signup) */}
            {verificationSent && step === 'email' && (
              <motion.div
                key="verification-sent"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="text-center"
              >
                <div className="mb-6">
                  <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Mail className="w-8 h-8 text-green-400" />
                  </div>
                  <h2 className="text-2xl font-semibold text-white mb-2">Check Your Email!</h2>
                  <p className="text-purple-300 mb-4">
                    We've sent a verification link to:
                  </p>
                  <p className="text-white font-medium mb-6">{email}</p>

                  <div className="bg-purple-900/30 border border-purple-500/30 rounded-lg p-4 mb-6 text-left">
                    <h3 className="text-white font-semibold mb-2">Next Steps:</h3>
                    <ol className="text-purple-200 text-sm space-y-2">
                      <li>1. Check your email inbox <strong>(and spam folder!)</strong></li>
                      <li>2. Click the verification link in the email</li>
                      <li>3. You'll be redirected back to sign in</li>
                      <li>4. Enter your password to access your account</li>
                    </ol>
                  </div>

                  <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-3 mb-4">
                    <p className="text-blue-200 text-sm">
                      <strong>Note:</strong> You must verify your email before you can sign in. The verification link expires in 24 hours.
                    </p>
                  </div>
                </div>

                <button
                  onClick={() => {
                    setVerificationSent(false);
                    setStep('email');
                    setEmail('');
                    setPassword('');
                    setFirstName('');
                    setLastName('');
                  }}
                  className="text-purple-300 hover:text-white block mx-auto"
                >
                  Use a different email
                </button>
              </motion.div>
            )}

            {/* Signup Step */}
            {step === 'signup' && !verificationSent && (
              <motion.form
                key="signup"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                onSubmit={handleSignUp}
              >
                <button
                  type="button"
                  onClick={() => setStep('email')}
                  className="flex items-center gap-2 text-purple-300 hover:text-white mb-4 transition"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Back
                </button>

                <h2 className="text-2xl font-semibold text-white mb-6">Create Account</h2>

                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-purple-200 mb-2">
                        First Name
                      </label>
                      <input
                        type="text"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        className="w-full px-4 py-3 bg-white/5 border border-purple-500/30 rounded-lg text-white placeholder-purple-300/50 focus:outline-none focus:border-purple-400 focus:bg-white/10 transition text-base sm:text-sm"
                        placeholder="John"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-purple-200 mb-2">
                        Last Name
                      </label>
                      <input
                        type="text"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        className="w-full px-4 py-3 bg-white/5 border border-purple-500/30 rounded-lg text-white placeholder-purple-300/50 focus:outline-none focus:border-purple-400 focus:bg-white/10 transition text-base sm:text-sm"
                        placeholder="Doe"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-purple-200 mb-2">
                      Email Address
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-purple-400 w-5 h-5" />
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full pl-10 pr-4 py-3 bg-white/5 border border-purple-500/30 rounded-lg text-white placeholder-purple-300/50 focus:outline-none focus:border-purple-400 focus:bg-white/10 transition text-base sm:text-sm"
                        placeholder="you@example.com"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-purple-200 mb-2">
                      Password
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-purple-400 w-5 h-5" />
                      <input
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full pl-10 pr-12 py-3 bg-white/5 border border-purple-500/30 rounded-lg text-white placeholder-purple-300/50 focus:outline-none focus:border-purple-400 focus:bg-white/10 transition"
                        placeholder="Choose a strong password"
                        required
                        minLength={6}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-purple-400 hover:text-purple-300 transition"
                        aria-label={showPassword ? "Hide password" : "Show password"}
                      >
                        {showPassword ? (
                          <EyeOff className="w-5 h-5" />
                        ) : (
                          <Eye className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                  </div>

                  {displayError && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-200 text-sm flex items-start gap-2"
                    >
                      <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                      <span>{displayError}</span>
                    </motion.div>
                  )}

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-lg hover:from-purple-700 hover:to-pink-700 transition flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {isLoading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        Create Account
                        <Sparkles className="w-5 h-5" />
                      </>
                    )}
                  </button>
                </div>

                {/* OAuth Divider */}
                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-purple-500/30" />
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="bg-gradient-to-br from-purple-900 via-indigo-900 to-blue-900 px-3 text-purple-300">
                      Or sign up with
                    </span>
                  </div>
                </div>

                {/* OAuth Buttons */}
                <div className="grid grid-cols-3 gap-3">
                  <button
                    type="button"
                    onClick={() => handleOAuthLogin('google')}
                    disabled={isLoading}
                    className="px-4 py-3 bg-white/10 border border-purple-500/30 text-white rounded-lg hover:bg-white/20 transition flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    <Chrome className="w-5 h-5" />
                    <span className="text-sm font-medium">Google</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleOAuthLogin('linkedin')}
                    disabled={isLoading}
                    className="px-4 py-3 bg-white/10 border border-purple-500/30 text-white rounded-lg hover:bg-white/20 transition flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    <Linkedin className="w-5 h-5" />
                    <span className="text-sm font-medium">LinkedIn</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleOAuthLogin('github')}
                    disabled={isLoading}
                    className="px-4 py-3 bg-white/10 border border-purple-500/30 text-white rounded-lg hover:bg-white/20 transition flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    <Github className="w-5 h-5" />
                    <span className="text-sm font-medium">GitHub</span>
                  </button>
                </div>

                <div className="mt-6 text-center">
                  <p className="text-purple-300 text-sm">
                    Already have an account?{' '}
                    <button
                      type="button"
                      onClick={() => setStep('email')}
                      className="text-pink-400 hover:text-pink-300 font-medium"
                    >
                      Sign in
                    </button>
                  </p>
                </div>
              </motion.form>
            )}
          </AnimatePresence>
        </motion.div>
      </motion.div>
    </div>
  );
};